import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getPlaceDetails, getPlaceSuggestions } from "../../../actions/googleMaps";
import {
  getPlacePredictionLabel,
  newSessionToken,
  parseGoogleAddressComponents,
  toBoundsLiteral,
  toLatLng,
} from "../../../utils/googleMapsUtils";
import { useGoogleMapsApiKey } from "../googleMaps/googleMapsKeyContext";

const PLACE_FIELDS = "addressComponents,formattedAddress,location,viewport";

const GooglePlacesAutocomplete = ({
  handleSubmit,
  onPlaceSelected,
  onInputValueChange,
  initialInputValue = "",
  idArt,
  disabled,
  disablePortal = false,
  freeSolo = false,
  fullWidth = true,
  sx,
  ...textFieldProps
}) => {
  const dispatch = useDispatch();
  const { apiKey } = useGoogleMapsApiKey(idArt);
  const [inputValue, setInputValue] = useState(initialInputValue);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const sessionTokenRef = useRef(null);
  const requestIdRef = useRef(0);
  // Evita pedir predicciones para una dirección ya elegida o precargada.
  const selectedAddressRef = useRef(initialInputValue);

  useEffect(() => {
    setInputValue(initialInputValue);
    selectedAddressRef.current = initialInputValue;
  }, [initialInputValue]);

  useEffect(() => {
    if (
      !apiKey ||
      disabled ||
      inputValue.trim().length <= 3 ||
      inputValue === selectedAddressRef.current
    ) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    const timeoutId = window.setTimeout(async () => {
      try {
        setLoading(true);
        sessionTokenRef.current ??= newSessionToken();
        const suggestions = await dispatch(
          getPlaceSuggestions(apiKey, inputValue, sessionTokenRef.current, {
            restrictToAddress: true,
          }),
        );
        if (requestId !== requestIdRef.current) return;
        setOptions(suggestions?.value?.data || []);
      } catch {
        if (requestId === requestIdRef.current) setOptions([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [apiKey, disabled, dispatch, inputValue]);

  const handlePlaceChange = async (_event, prediction) => {
    if (!prediction || typeof prediction === "string") return;

    try {
      setLoading(true);
      const placeResult = await dispatch(
        getPlaceDetails(
          apiKey,
          prediction.placeId,
          sessionTokenRef.current,
          PLACE_FIELDS,
        ),
      );
      const place = placeResult?.value?.data;
      const address = {
        ...parseGoogleAddressComponents(place.addressComponents),
        formattedAddress:
          place.formattedAddress || getPlacePredictionLabel(prediction),
        placeId: prediction.placeId,
        location: toLatLng(place.location),
        viewport: toBoundsLiteral(place.viewport),
      };

      selectedAddressRef.current = address.formattedAddress;
      setInputValue(address.formattedAddress);
      setOptions([]);
      sessionTokenRef.current = null;
      handleSubmit?.(address);
      onPlaceSelected?.(address);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Autocomplete
      disabled={disabled}
      filterOptions={(items) => items}
      freeSolo={freeSolo}
      fullWidth={fullWidth}
      getOptionKey={(option) => option.placeId}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : getPlacePredictionLabel(option)
      }
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => option.placeId === value.placeId}
      loading={loading}
      loadingText="Buscando..."
      noOptionsText={
        inputValue.trim().length > 3 ? "Sin resultados" : "Ingresá una dirección"
      }
      onChange={handlePlaceChange}
      onInputChange={(_event, value, reason) => {
        if (reason === "input") selectedAddressRef.current = "";
        setInputValue(value);
        if (reason === "input") {
          onInputValueChange?.(value);
          if (!value) sessionTokenRef.current = null;
        }
      }}
      options={options}
      sx={sx}
      componentsProps={{
        popper: {
          disablePortal,
          sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          type="text"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={24} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default GooglePlacesAutocomplete;
